import { Component } from "@theme/component";

/**
 * @typedef {Object} SDTabsRefs
 * @property {HTMLElement} nav - Tab navigation container
 * @property {HTMLElement} panelsContainer - Panels wrapper
 */

/**
 * SD Tabs Web Component
 *
 * Dynamically builds tab navigation from sd-tab-panel blocks.
 * Handles tab interactions, keyboard navigation, and ARIA states.
 *
 * @extends {Component<SDTabsRefs>}
 */
class SDTabs extends Component {
  /** @type {HTMLButtonElement[]} */
  tabs = [];

  /** @type {HTMLElement[]} */
  panels = [];

  /** @type {number} */
  activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  #init() {
    const nav = this.querySelector(".sd-tabs__nav");
    const panelsContainer = this.querySelector(".sd-tabs__panels");

    if (!nav || !panelsContainer) return;

    this.nav = nav;
    this.panelsContainer = panelsContainer;

    this.#discoverPanels();
    this.#updateEmptyState();

    if (this.panels.length === 0) return;

    this.#buildTabs();

    const defaultTab = parseInt(this.dataset.defaultTab || "1", 10) || 1;
    const initialIndex = Math.min(
      Math.max(defaultTab - 1, 0),
      this.panels.length - 1,
    );
    this.activateTab(initialIndex, false);
  }

  #updateEmptyState() {
    const emptyState = this.querySelector(".sd-tabs__empty");
    if (!emptyState) return;

    const isInEditor = window.Shopify?.designMode === true;
    const hasPanels = this.panels.length > 0;

    if (isInEditor && !hasPanels) {
      emptyState.hidden = false;
      this.nav?.parentElement?.style.setProperty("display", "none");
    } else {
      emptyState.hidden = true;
      this.nav?.parentElement?.style.removeProperty("display");
    }
  }

  #discoverPanels() {
    if (!this.panelsContainer) return;
    this.panels = Array.from(
      this.panelsContainer.querySelectorAll(".sd-tabs__panel[data-tab-title]"),
    );
  }

  #buildTabs() {
    this.nav.innerHTML = "";
    this.tabs = [];

    for (const [index, panel] of this.panels.entries()) {
      const tabId = panel.dataset.tabId || `tab-${index}`;
      const title = panel.dataset.tabTitle || `Tab ${index + 1}`;

      const tab = document.createElement("button");
      tab.className = "sd-tabs__tab";
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", "false");
      tab.setAttribute("aria-controls", `sd-panel-${tabId}`);
      tab.setAttribute("id", `sd-tab-${tabId}`);
      tab.setAttribute("tabindex", "-1");
      tab.setAttribute("on:click", "/handleTabClick");
      tab.setAttribute("on:keydown", "/handleTabKeydown");
      tab.dataset.index = String(index);

      const titleSpan = document.createElement("span");
      titleSpan.className = "sd-tabs__tab-title";
      titleSpan.textContent = title;
      tab.appendChild(titleSpan);

      this.nav.appendChild(tab);
      this.tabs.push(tab);

      panel.setAttribute("id", `sd-panel-${tabId}`);
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", `sd-tab-${tabId}`);
    }
  }

  /**
   * Handle tab click events
   * @param {MouseEvent} event
   */
  handleTabClick(event) {
    const tab = event.target.closest(".sd-tabs__tab");
    if (!tab) return;

    const index = parseInt(tab.dataset.index, 10);
    this.activateTab(index);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const { key } = event;
    let newIndex = this.activeIndex;

    switch (key) {
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        newIndex =
          this.activeIndex === 0 ? this.tabs.length - 1 : this.activeIndex - 1;
        break;

      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        newIndex =
          this.activeIndex === this.tabs.length - 1 ? 0 : this.activeIndex + 1;
        break;

      case "Home":
        event.preventDefault();
        newIndex = 0;
        break;

      case "End":
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        break;

      case "Enter":
      case " ":
        event.preventDefault();
        this.activateTab(this.activeIndex);
        return;

      default:
        return;
    }

    this.activateTab(newIndex, true);
  }

  /**
   * Activate a specific tab
   * @param {number} index - Tab index to activate
   * @param {boolean} [focus=true] - Whether to focus the tab
   */
  activateTab(index, focus = true) {
    if (index < 0 || index >= this.tabs.length) return;

    for (const [i, tab] of this.tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    }

    for (const [i, panel] of this.panels.entries()) {
      const isActive = i === index;
      panel.dataset.active = isActive ? "true" : "false";
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    }

    this.activeIndex = index;

    if (focus && this.tabs[index]) {
      this.tabs[index].focus();
    }

    this.dispatchEvent(
      new CustomEvent("tab-change", {
        detail: {
          index,
          tab: this.tabs[index],
          panel: this.panels[index],
        },
        bubbles: true,
      }),
    );
  }

  /**
   * Get the currently active tab
   * @returns {HTMLButtonElement|undefined}
   */
  getActiveTab() {
    return this.tabs[this.activeIndex];
  }

  /**
   * Get the currently active panel
   * @returns {HTMLElement|undefined}
   */
  getActivePanel() {
    return this.panels[this.activeIndex];
  }
}

if (!customElements.get("sd-tabs")) {
  customElements.define("sd-tabs", SDTabs);
}
